import lodashGet from "lodash/get";
import lodashFind from "lodash/find";
import lodashForOwn from "lodash/forOwn";

import { removeHtmlTags, upperCaseFirstLetter } from "@/common/basic.utilities";
import { SHARE_OPTIONS } from "@/common/constants/alert.js";
import { getSources } from "@/services/alerts/alerts.service.js";
import { getPrimaryTopics, findTopic } from "@/services/events/topics.service";

export { _formatContentFeatures as formatContentFeatures };
export { _formatAlertApiToUiObject as formatAlertApiToUiObject };
export { _formatAlertUiTOApi as formatAlertUiTOApi };

const _formatContentFeatures = (feature) => {
  if (feature.properties) {
    const sourceDetails = feature.properties;
    if (sourceDetails.contentType === "verified-event") {
      const eventType = lodashGet(sourceDetails, "verifiedEventDetails.type")
        ? sourceDetails.verifiedEventDetails.type
        : null;
      feature.properties.formatted = {
        title: sourceDetails.title || null,
        summary: sourceDetails.summary || null,
        language: "en",
        translated: true,
        eventType: eventType,
        categories:
          lodashGet(sourceDetails, "verifiedEventDetails.categories") || null,
        regionName:
          lodashGet(sourceDetails, "verifiedEventDetails.regionName") || null,
      };
      if (eventType === "political") {
        const country =
          lodashGet(sourceDetails, "verifiedEventDetails.countryName") || null;
        feature.properties.formatted.country = country;
        feature.properties.formatted.severityScore =
          lodashGet(sourceDetails, "verifiedEventDetails.severityScore") ||
          null;
        feature.properties.formatted.assetTypes =
          lodashGet(sourceDetails, "verifiedEventDetails.assetTypes") || null;
        feature.properties.formatted.damageValue =
          lodashGet(sourceDetails, "verifiedEventDetails.damageValue") || null;
        feature.properties.formatted.attackTypes =
          lodashGet(sourceDetails, "verifiedEventDetails.attackTypes") || null;
      } else {
        const locality =
          lodashGet(sourceDetails, "verifiedEventDetails.locality") || null;
        feature.properties.formatted.securityInterventionTypes =
          lodashGet(
            sourceDetails,
            "verifiedEventDetails.securityInterventionTypes"
          ) || null;
        feature.properties.formatted.weaponTypes =
          lodashGet(sourceDetails, "verifiedEventDetails.weaponTypes") || null;
        feature.properties.formatted.locality = locality;
        feature.properties.formatted.portName =
          lodashGet(sourceDetails, "verifiedEventDetails.portName") || null;
        feature.properties.formatted.waterName =
          lodashGet(sourceDetails, "verifiedEventDetails.waterName") || null;
        feature.properties.formatted.stolenItems =
          lodashGet(sourceDetails, "verifiedEventDetails.stolenItems") || null;
        feature.properties.formatted.subCategories =
          lodashGet(sourceDetails, "verifiedEventDetails.subCategories") ||
          null;
        feature.properties.formatted.vesselName =
          lodashGet(sourceDetails, "verifiedEventDetails.vesselName") || null;
      }
    } else {
      feature.properties.formatted = {
        esId: sourceDetails.id || null,
        title: sourceDetails.title ? removeHtmlTags(sourceDetails.title) : null,
        summary: sourceDetails.summary
          ? removeHtmlTags(sourceDetails.summary)
          : null,
        language: sourceDetails.language,
        translated: sourceDetails.translated || false,
        source: sourceDetails.origin || "View Article",
        img: sourceDetails.imageUrl || null,
        link: sourceDetails.url,
        topic_scores: sourceDetails.topic_scores
          ? sourceDetails.topic_scores
          : null,
      };

      feature.properties.formatted.sourceVeracity = null;
      if (lodashGet(feature, "properties.veracity")) {
        feature.properties.formatted.sourceVeracity =
          feature.properties.veracity;
      }
    }

    feature.properties.formatted.esId = sourceDetails.id || null;
    feature.properties.formatted.occurred = sourceDetails["@timestamp"] || null;
    feature.properties.formatted.type = sourceDetails.contentType;
    feature.properties.formatted.location = lodashGet(
      feature,
      "geometry.coordinates"
    )
      ? lodashGet(feature, "geometry.coordinates")
      : [];
    feature.properties.formatted.read = sourceDetails.read ? true : false;
    feature.properties.formatted.documentId = sourceDetails.documentId
      ? sourceDetails.documentId
      : null;
    feature.properties.formatted.triggeredId = sourceDetails.triggeredId
      ? sourceDetails.triggeredId
      : null;
  }
  return feature;
};

const _addKeywordInformation = (apiAlert, uiAlert) => {
  uiAlert.keywords = {
    includes: apiAlert.keywordInfo.sets.map((item) => {
      return { values: item.include };
    }),
    excludes: apiAlert.keywordInfo.exclude,
  };
  return uiAlert;
};

const _formatToUIEvents = (apiAlert, uiAlert) => {
  uiAlert.eventsSelection = {};
  if (
    lodashGet(apiAlert, "keywordInfo.subtype") &&
    Array.isArray(apiAlert.keywordInfo.subtype) &&
    apiAlert.keywordInfo.subtype.length
  ) {
    // subtype example: conflict:armed-conflict
    apiAlert.keywordInfo.subtype.forEach((subtype) => {
      const PARTS = subtype.split(":");
      const TYPE = PARTS[0];
      const SUB = PARTS[1];
      uiAlert.eventsSelection[TYPE] = uiAlert.eventsSelection[TYPE]
        ? uiAlert.eventsSelection[TYPE]
        : {
            useMainTopic: false,
            categories: [],
          };
      const TOPIC = findTopic(TYPE);
      if (lodashGet(TOPIC, "legacyAlertCategories")) {
        if (TYPE && !SUB) {
          //NOTE: Main topic selected
          uiAlert.eventsSelection[TYPE].useMainTopic = true;
          uiAlert.eventsSelection[
            TYPE
          ].categories = TOPIC.legacyAlertCategories.map((cat) => cat.id);
        } else {
          //NOTE: Subtopics Selected
          uiAlert.eventsSelection[TYPE].categories.push(SUB.toLowerCase());
          //NOTE: The following line is catch old alerts that may have been expired that have all sub topics of
          // one topic selected.
          uiAlert.eventsSelection[TYPE].useMainTopic =
            uiAlert.eventsSelection[TYPE].categories.length ===
            TOPIC.legacyAlertCategories.length;
        }
      }
    });
  }
  return uiAlert;
};

const _formatToUIVerifiedEvents = (apiAlert, uiAlert) => {
  uiAlert.verifiedEventsSelection = lodashGet(apiAlert, "verifiedEvents") || {};
  return uiAlert;
};

const _setShareInfo = (apiAlert, uiAlert) => {
  uiAlert.share = {
    type: SHARE_OPTIONS[0],
    list: [],
  };
  if (
    apiAlert.shareInfo &&
    apiAlert.shareInfo.with &&
    apiAlert.shareInfo.with.length > 0
  ) {
    uiAlert.share.type = SHARE_OPTIONS[1];
  }
  if (
    uiAlert.share.type.id === SHARE_OPTIONS[1].id &&
    apiAlert.shareInfo &&
    apiAlert.shareInfo.via &&
    apiAlert.shareInfo.via.indexOf("email") > -1
  ) {
    uiAlert.share.type = SHARE_OPTIONS[2];
  }
  uiAlert.share.list = apiAlert.shareInfo.with;
};

const _removeUnsupportedSources = (uiAlert) => {
  if (uiAlert.sources) {
    const SOURCES = getSources();
    uiAlert.sources = uiAlert.sources.filter((item) => {
      return lodashFind(SOURCES, item);
    });
  }
  return uiAlert;
};

const _formatAlertApiToUiObject = (apiAlert) => {
  const FEATURE_NAME = lodashGet(apiAlert, "polygons[0].name")
    ? apiAlert.polygons[0].name
    : null;
  const FEATURE_GEOMETRY = lodashGet(apiAlert, "polygons[0].coordinates")
    ? {
        type: "Polygon",
        coordinates: [apiAlert.polygons[0].coordinates],
      }
    : null;

  const uiAlert = {
    id: apiAlert.name,
    name: apiAlert.description,
    sendEmail: apiAlert.sendEmail,
    unsubToken: apiAlert.unsubToken,
    owner: {
      id: apiAlert.owner,
      email: apiAlert.ownerEmail,
    },
    auditInfo: apiAlert.auditInfo,
    aoiFeature: {
      type: "Feature",
      properties: {
        name: FEATURE_NAME,
        aoiIds: apiAlert.aoiIds || null,
      },
      geometry: FEATURE_GEOMETRY,
    },
    sources: apiAlert.sources
      ? apiAlert.sources.map((s) => {
          if (s.hasOwnProperty("text")) {
            delete s.text;
          }
          if (s.hasOwnProperty("isSelected")) {
            delete s.isSelected;
          }
          s.display = s.display.toUpperCase();
          if (s.id === "twitter") {
            s.display = "SOCIAL";
          }
          return s;
        })
      : null,
    frequency: apiAlert.keywordInfo ? apiAlert.keywordInfo.frequencyOpts : {},
    stabilityThreshold: {},
    schedule: apiAlert.schedule,
    share: {},
    keywords: {
      includes: [],
      excludes: [],
    },
    verifiedEvents: {
      type: null,
      categories: [],
    },
  };
  _setShareInfo(apiAlert, uiAlert);
  _removeUnsupportedSources(uiAlert);

  uiAlert.expiration = apiAlert.expirationTimeframe;
  uiAlert.expiration.date = apiAlert.expirationDate;

  const validTypes = [
    "keywords",
    "hotspots",
    "events",
    "verifiedEvents",
    "pulse",
  ];
  const apiAlertTypes = apiAlert.alertWizardType.split(",");
  uiAlert.hideEditControls = !apiAlertTypes.reduce((accum, item) => {
    return accum && validTypes.indexOf(item) > -1;
  }, true);

  apiAlertTypes.map((item) => {
    switch (item) {
      case "keywords":
        _addKeywordInformation(apiAlert, uiAlert);
        break;
      case "hotspots":
        uiAlert.hotspotsEnabled = true;
        break;
      case "pulse":
        uiAlert.pulseEnabled = true;
        break;
    }
  });

  if (apiAlert.pulseDetails) {
    uiAlert.pulseDetails = apiAlert.pulseDetails;
  }

  _formatToUIEvents(apiAlert, uiAlert);
  _formatToUIVerifiedEvents(apiAlert, uiAlert);

  return uiAlert;
};

const _formatApiShareInformation = (uiAlert, apiAlert) => {
  const NO_SHARE_ID = 1;
  const SHARE_WITH_EMAIL_ID = 3;
  apiAlert.shareInfo = {
    via: [],
    with: [],
  };
  if (!uiAlert.share || !uiAlert.share.type) {
    return;
  }
  if (uiAlert.share.type.id === NO_SHARE_ID) {
    return;
  }
  if (uiAlert.share.type.id === SHARE_WITH_EMAIL_ID) {
    apiAlert.shareInfo.via.push("email");
  }
  if (uiAlert.share.list && uiAlert.share.list.length > 0) {
    apiAlert.shareInfo.with = uiAlert.share.list;
  }
};

const _formatBasicApiAlertSettings = (uiAlert) => {
  const apiAlert = {
    description: uiAlert.name,
    sendEmail: uiAlert.sendEmail,
    sources: uiAlert.sources,
    owner: uiAlert.owner ? uiAlert.owner.id : null,
    ownerEmail: uiAlert.owner ? uiAlert.owner.email : null,
    auditInfo: uiAlert.auditInfo,
    aoiIds: uiAlert.aoiFeature.properties.aoiIds,
    polygons: [],
    expirationTimeframe: uiAlert.expiration,
    qid: [],
    activityInfo: {
      level: null,
      threshold: null,
    },
    stabilityInfo: {
      level: null,
      threshold: null,
    },
    enabled: true,
  };

  if (lodashGet(uiAlert, "aoiFeature.geometry.coordinates[0]")) {
    apiAlert.polygons = [
      {
        name: uiAlert.aoiFeature.properties.name,
        coordinates: uiAlert.aoiFeature.geometry.coordinates[0],
      },
    ];
  }

  if (uiAlert.schedule && uiAlert.schedule.length > 0) {
    apiAlert.schedule = uiAlert.schedule;
  }

  if (uiAlert.id) {
    apiAlert.name = uiAlert.id;
  }

  apiAlert.keywordInfo = {
    subtype: [],
    sets: [],
    exclude: [],
    frequencyOpts: uiAlert.frequency,
    limit: 1,
    thresholdOpts: {
      id: "frequency",
      display: "GREATER THAN OR EQUAL TO",
    },
    quickValueTopic: null,
  };
  apiAlert.timeframe = parseInt(uiAlert.frequency.id);

  _formatApiShareInformation(uiAlert, apiAlert);

  return apiAlert;
};

const _formatToApiKeywordAlert = (uiAlert, apiAlert) => {
  apiAlert.keywordInfo.exclude = uiAlert.keywords.excludes;
  apiAlert.keywordInfo.sets = uiAlert.keywords.includes.map((item) => {
    return {
      include: item.values,
    };
  });
};

const _formatToApiEvents = (uiAlert, apiAlert) => {
  const subtypes = [];
  const PRIMARY_TOPICS = getPrimaryTopics();
  PRIMARY_TOPICS.forEach((topic) => {
    const eventSelection = uiAlert.eventsSelection[topic.type];
    if (!eventSelection) {
      return;
    }
    if (eventSelection.useMainTopic && subtypes.indexOf(topic.type) === -1) {
      subtypes.push(topic.type);
      return;
    }
    if (!Array.isArray(eventSelection.categories)) {
      return;
    }
    const CATEGORIES = eventSelection.categories;
    CATEGORIES.forEach((category) => {
      subtypes.push(`${topic.type}:${category}`);
    });
  });
  apiAlert.keywordInfo.subtype = subtypes;
};

const _formatToApiVerifiedEvents = (uiAlert, apiAlert) => {
  apiAlert.verifiedEvents = lodashGet(uiAlert, "verifiedEventsSelection") || {};
};

const _formatToApiStabilityAlert = (uiAlert, apiAlert) => {
  apiAlert.stabilityInfo = {
    level: uiAlert.stabilityThreshold,
    threshold: {
      id: "max",
      display: "Above",
    },
  };

  apiAlert.activityGrid = {
    gridType: "stabilityGrid",
    timeBinSize: "1d",
    precision: 3,
  };

  apiAlert.metric_agg_type = "avg";
  apiAlert.metric_agg_threshold_type = "max";
};

const _isKeywordAlert = (uiAlert) => {
  const hasIncludes = uiAlert.keywords.includes.reduce((accum, item) => {
    return accum || item.values.length > 0;
  }, false);

  const hasExcludes = uiAlert.keywords.excludes.reduce((accum, item) => {
    return accum || item.text.length > 0;
  }, false);
  return hasExcludes || hasIncludes;
};

const _isEventsAlert = (uiAlert) => {
  let hasEvents = false;
  lodashForOwn(uiAlert.eventsSelection, (value) => {
    if (
      value &&
      value.categories &&
      Array.isArray(value.categories) &&
      value.categories.length
    ) {
      hasEvents = true;
    }
  });
  return hasEvents;
};

const _isVerifiedEventsAlert = (uiAlert) => {
  let hasVerifiedEvents = false;
  if (lodashGet(uiAlert, "verifiedEventsSelection.type")) {
    hasVerifiedEvents =
      uiAlert.verifiedEventsSelection.type === "maritime" ||
      (uiAlert.verifiedEventsSelection.type === "security" &&
        lodashGet(uiAlert, "verifiedEventsSelection.categories") &&
        Array.isArray(uiAlert.verifiedEventsSelection.categories) &&
        !!uiAlert.verifiedEventsSelection.categories.length);
  }
  return hasVerifiedEvents;
};

const _formatAlertUiTOApi = (uiAlert) => {
  const apiAlert = _formatBasicApiAlertSettings(uiAlert);
  const alertWizardTypes = [];
  const alertTypes = [];
  if (_isKeywordAlert(uiAlert)) {
    alertWizardTypes.push("keywords");
    alertTypes.push("frequency");
    _formatToApiKeywordAlert(uiAlert, apiAlert);
  }

  if (_isEventsAlert(uiAlert)) {
    alertWizardTypes.push("events");
    alertTypes.push("frequency");
    _formatToApiEvents(uiAlert, apiAlert);
  }

  if (_isVerifiedEventsAlert(uiAlert)) {
    alertWizardTypes.push("verifiedEvents");
    alertTypes.push("frequency");
    _formatToApiVerifiedEvents(uiAlert, apiAlert);
  }

  if (uiAlert.hotspotsEnabled) {
    alertWizardTypes.push("hotspots");
    alertTypes.push("frequency");
  }

  if (uiAlert.pulseEnabled) {
    alertWizardTypes.push("pulse");
    alertTypes.push("frequency");
  }

  if (uiAlert.stabilityThreshold.id > 0) {
    alertWizardTypes.push("stability");
    alertTypes.push("metric_aggregation");
    _formatToApiStabilityAlert(uiAlert, apiAlert);
  }

  if (uiAlert.pulseDetails) {
    apiAlert.pulseDetails = uiAlert.pulseDetails;
  }

  apiAlert.alertWizardType = alertWizardTypes.join(",");
  apiAlert.alertType = alertTypes.join(",");

  return [apiAlert];
};
